import React from 'react';
import { Control, Controller, ControllerProps, FieldError } from 'react-hook-form';
import { TextareaAutosize, TextField, TextFieldProps } from '@mui/material';
import { useStyles } from './styles';
import { getTranslateByKey } from '@/main/services/utils';
import { uppercaseFirstLetter } from '@/main/services/utilsString';

type IControllerProps = Omit<ControllerProps, 'render' | 'name'>;

type TextAreaProps = {
  name: string;
  control: Control<any>;
  controllerProps?: IControllerProps;
  label: string;
  rulesCustom: { required: boolean };
  errorMessageCustom?: string;
  hasOnlyNumber?: boolean;
  disabledMessage?: boolean;
  onChangeCustom?: (value: string) => void;
} & TextFieldProps;

const KogTextArea = ({
  name,
  control,
  label,
  controllerProps,
  rulesCustom,
  errorMessageCustom,
  hasOnlyNumber = false,
  disabledMessage = false,
  onChangeCustom = () => { },
  ...props }: TextAreaProps) => {
  const styles = useStyles({});
  const renderErrorMessages = (error: FieldError | undefined) => {
    const requiredMessage = getTranslateByKey('information.isRequired', true)
    const fieldMessage = getTranslateByKey('information.field')

    if (!error) return null;
    switch (error.type) {
      case 'required':
        return uppercaseFirstLetter(requiredMessage.replace('{0}', `${String(label || fieldMessage)}`));
      case 'minLength':
        return `${label || 'This Field'} must have at least ${controllerProps?.rules?.minLength} digits`;
      default:
        return error.message;
    }
  };

  function handleValueChange(value: string) {
    if (hasOnlyNumber && value) {
      const onlyNumbers = value.replace(/\D/g, ''); // Remove characters that are not numbers
      return onlyNumbers;
    }
    return value;
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rulesCustom}
      {...controllerProps}
      render={({
        field: { ref, ...field },
        fieldState: { error }
      }) => (
        <TextField
          fullWidth
          required={Boolean(controllerProps?.rules?.required)}
          size='small'
          variant='filled'
          multiline
          minRows={3}
          error={!!error}
          label={label}
          helperText={
            !disabledMessage &&
            (errorMessageCustom || renderErrorMessages(error))
          }
          className={styles.inputText}
          inputProps={props.inputProps ? props.inputProps : { maxLength: 100 }}
          inputRef={ref}
          {...field}
          {...props}
          onChange={(e) => {
            const validateValue = handleValueChange(e.target.value);

            field.onChange(validateValue);
            onChangeCustom(validateValue);
          }}
          value={field.value}
        />
      )}
    />
  );
};

export default KogTextArea;
